import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, CalendarIcon, CalendarDays, ChartColumn, Clock3, Download, Funnel, LogIn, LogOut, RefreshCw, Timer, X } from "lucide-react";
import { format } from "date-fns";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import { Calendar } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { ContentCard, DataTable, PageHeader, ui } from "../../../components/ui";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import { downloadAttendanceReport, getReport } from "../../../services/attendanceService";
import { formatDate, formatDuration, formatTime } from "../utils/dateUtils";
import { TimeLogModal } from "../components/TimeLogModal";
import { ViewReportModal } from "../components/ViewReportModal";
import { TomorrowPlanModal } from "../components/TomorrowPlanModal";

// Added to allow marking the attendance only from mobile browsers. "N" means allow, "Y" means done allow..
const restrictOnDesktop = "N";

function isMobileBrowser() {
  if (navigator.userAgentData?.mobile !== undefined) {
    return navigator.userAgentData.mobile;
  }

  const isMobileUserAgent = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isMobileUserAgent || isIPadOS;
}


function toDateInputStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseYMD(value) {
  if (!value) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function hasAppliedRegularization(status) {
  return !!status && status !== "Rejected";
}

export function AttendanceListPage() {
  const navigate = useNavigate();
  const [defaultFilters] = useState(() => {
    const today = new Date();
    return {
      startDate: toDateInputStr(today),
      endDate: toDateInputStr(today)
    };
  });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  // "day" = default view, navigated via Prev/Next single-day controls.
  // "range" = a custom filter has been applied; Prev/Next are hidden.
  const [viewMode, setViewMode] = useState("day");
  const [timeLogRecord, setTimeLogRecord] = useState(null);
  const [viewReportRecord, setViewReportRecord] = useState(null);
  const [tomorrowPlanRecord, setTomorrowPlanRecord] = useState(null);

  useEffect(() => {
    const empId = getAuthenticatedEmployeeId();
    if (!empId) {
      setError("Unable to identify logged-in employee.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getReport(appliedFilters.startDate, appliedFilters.endDate)
    .then((data) => setRecords(Array.isArray(data) ? data : data ? [data] : []))
    .catch((err) => setError(err.message))
    .finally(() => setLoading(false));
  }, [appliedFilters]);

  const isNextDayDisabled = useMemo(() => {
    const cur = parseYMD(appliedFilters.startDate);
    if (!cur) return true;
    cur.setDate(cur.getDate() + 1);
    return toDateInputStr(cur) > toDateInputStr(new Date());
  }, [appliedFilters.startDate]);

  const goToPrevDay = () => {
    const cur = parseYMD(appliedFilters.startDate);
    if (!cur) return;
    cur.setDate(cur.getDate() - 1);
    const dateStr = toDateInputStr(cur);
    setAppliedFilters({ startDate: dateStr, endDate: dateStr });
  };

  const goToNextDay = () => {
    if (isNextDayDisabled) return;
    const cur = parseYMD(appliedFilters.startDate);
    if (!cur) return;
    cur.setDate(cur.getDate() + 1);
    const dateStr = toDateInputStr(cur);
    setAppliedFilters({ startDate: dateStr, endDate: dateStr });
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.startDate !== defaultFilters.startDate) count += 1;
    if (appliedFilters.endDate !== defaultFilters.endDate) count += 1;
    return count;
  }, [appliedFilters, defaultFilters]);

  const resetFilters = () => {
    setDraftFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setViewMode("day");
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError("");
    try {
      await downloadAttendanceReport(appliedFilters.startDate, appliedFilters.endDate, "Self", "");
    } catch (err) {
      setDownloadError(err.message || "Failed to download attendance report.");
    } finally {
      setDownloading(false);
    }
  };

  const applyFilters = () => {
    setAppliedFilters({
      startDate: draftFilters.startDate,
      endDate: draftFilters.endDate
    });
    setViewMode("range");
    setFilterModalOpen(false);
  };

  const handleMarkAttendance = () => {
    if (restrictOnDesktop === "Y" && !isMobileBrowser()) {
      window.alert("Please mark your attendance from mobile browser only.");
      return;
    }

    navigate("/hrms-ess/attendance/mark");
  };

  return (
    <AppLayout
      overlay={
      <>
          <Dialog open={filterModalOpen} onOpenChange={setFilterModalOpen}>
            <DialogPortal>
              <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
              <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-[95vw] sm:w-[90vw] md:w-[520px] max-w-[520px] bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60 overflow-hidden font-sans">
                <div className="flex items-center justify-between px-6 py-3.5 border-b border-(--color-text-border)/70">
                  <DialogTitle className="text-[20px] font-semibold text-foreground tracking-tight">
                    <Trans i18nKey="formActions.filterMyAttendance" />
                  </DialogTitle>
                  <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl per-file-hint hover:bg-workspace/60 hover:text-primary transition">
                    <X className="h-5 w-5" />
                    <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
                  </DialogPrimitive.Close>
                </div>
                <div className="px-6 py-5 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-foreground"><Trans i18nKey="dateTime.fromDate" /></label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "h-11 w-full justify-start text-left font-normal rounded-xl border border-(--color-text-border) bg-white px-3",
                            !draftFilters.startDate && "text-muted-foreground"
                          )}>

                            <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
                            {draftFilters.startDate ?
                          format(parseYMD(draftFilters.startDate), "PPP") :

                          <span><Trans i18nKey="dateTime.selectDate" /></span>
                          }
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                          <Calendar
                            mode="single"
                            selected={parseYMD(draftFilters.startDate)}
                            defaultMonth={parseYMD(draftFilters.startDate)}
                            onSelect={(dateObj) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                startDate: toDateInputStr(dateObj)
                              }))
                            }
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />

                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-foreground"><Trans i18nKey="dateTime.toDate" /></label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "h-11 w-full justify-start text-left font-normal rounded-xl border border-(--color-text-border) bg-white px-3",
                            !draftFilters.endDate && "text-muted-foreground"
                          )}>

                            <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
                            {draftFilters.endDate ?
                          format(parseYMD(draftFilters.endDate), "PPP") :

                          <span><Trans i18nKey="dateTime.selectDate" /></span>
                          }
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                          <Calendar
                            mode="single"
                            selected={parseYMD(draftFilters.endDate)}
                            defaultMonth={parseYMD(draftFilters.endDate)}
                            onSelect={(dateObj) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                endDate: toDateInputStr(dateObj)
                              }))
                            }
                            disabled={(dateObj) =>
                              dateObj < parseYMD(draftFilters.startDate)
                            }
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />

                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-(--color-text-border)/70 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
                  <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDraftFilters(defaultFilters)}
                  className="h-11 px-5 rounded-xl border border-(--color-text-border) bg-white text-sm font-semibold per-file-hint hover:text-primary hover:border-primary/40">
                    <Trans i18nKey="formActions.clearFilters" />

                </Button>
                  <Button
                  type="button"
                  onClick={applyFilters}
                  className="h-11 px-5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-accent transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]">
                    <Trans i18nKey="formActions.applyFilters" />

                </Button>
                </div>
              </DialogPrimitive.Content>
            </DialogPortal>
          </Dialog>
        </>
      }>


      <PageHeader back={<PageBackLink to="hrms-team/manager" />}
        title={i18n.t("attendance.attendanceManagement")}
        action={
        <Button
          className="h-11 px-5 rounded-xl bg-primary text-white hover:bg-accent cursor-pointer"
          onClick={handleMarkAttendance}>

            <Clock3 className="h-4 w-4" /> <Trans i18nKey="formActions.markAttendance" />
          </Button>
        } />


      <ContentCard
        title={i18n.t("attendance.myAttendance")}
        action={
        <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              aria-busy={downloading}
              className={cn(ui.filterBtn, "disabled:opacity-50 disabled:cursor-not-allowed")}>
              <Download className="h-4 w-4" />
              <span>{downloading ? "Downloading..." : "Download"}</span>
            </button>
            <button
            type="button"
            onClick={() => {
              setDraftFilters(appliedFilters);
              setFilterModalOpen(true);
            }}
            className={ui.filterBtn}>

              <Funnel className="h-4 w-4" />
              <span><Trans i18nKey="formActions.filter" /></span>
              {activeFilterCount > 0 ?
            <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-white">
                  {activeFilterCount}
                </span> :
            null}
            </button>
            <button
            type="button"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            className={cn(ui.filterBtn, "disabled:opacity-50 disabled:cursor-not-allowed")}>

              <RefreshCw className="h-4 w-4" />
              <span><Trans i18nKey="formActions.resetFilters" /></span>
            </button>
          </div>
        }
        bodyClassName="">

        {downloadError && (
          <p role="alert" className="px-4 py-3 text-sm text-error">{downloadError}</p>
        )}

        <DataTable paginate
          columns={[
          { key: "date", label: "Date" },
          { key: "checkIn", label: "Check In" },
          { key: "checkOut", label: "Check Out" },
          { key: "location", label: "Check In Location" },
          { key: "duration", label: "Duration" },
          { key: "timeLog", label: "Time Log" },
          { key: "report", label: "Report" },
          { key: "tomorrowPlan", label: "Tomorrow Plan" },
          { key: "action", label: "Actions", className: "text-right" }]
          }>

          {loading &&
          <tr>
              <td colSpan={9} className="px-4 py-8 text-center per-file-hint">
                <Trans i18nKey="attendance.loadingAttendanceRecords" />
              </td>
            </tr>
          }
          {!loading && error &&
          <tr>
              <td colSpan={9} className="px-4 py-8 text-center text-error">
                {error}
              </td>
            </tr>
          }
          {!loading && !error && records.length === 0 &&
          <tr>
              <td colSpan={9} className="px-4 py-8 text-center per-file-hint">
                <Trans i18nKey="attendance.noAttendanceRecordsFound" />
              </td>
            </tr>
          }
          {!loading &&
          !error &&
          records.map((record, i) => {
            const dateStr =
            record.attendanceDate || record.date || (record.checkInTime ? record.checkInTime.split("T")[0] : "");
            return (
              <tr
                key={record.attendanceId || i}>

                  <td className="px-4 py-3.5">{formatDate(dateStr)}</td>
                  <td className="px-4 py-3.5 font-mono">{formatTime(record.checkInTime)}</td>
                  <td className="px-4 py-3.5 font-mono">{formatTime(record.checkOutTime)}</td>
                  <td className="px-4 py-3.5">{record.checkInLocation || "—"}</td>
                  <td className="px-4 py-3.5 font-mono">{formatDuration(record.totalWorkMinutes ?? record.workMinutes)}</td>
                  <td className="px-4 py-3.5">
                    <Button
                    variant="outline"
                    className="h-9 rounded-xl px-3 text-xs font-semibold border-(--color-text-border) hover:border-primary/40 hover:text-primary cursor-pointer"
                    onClick={() => setTimeLogRecord(record)}>

                      <Clock3 className="h-3.5 w-3.5" /> <Trans i18nKey="attendance.viewTimeLog" />
                    </Button>
                  </td>
                  <td className="px-4 py-3.5">
                    <Button
                    variant="outline"
                    className="h-9 rounded-xl px-3 text-xs font-semibold border-(--color-text-border) hover:border-primary/40 hover:text-primary cursor-pointer"
                    onClick={() => setViewReportRecord(record)}>

                      <ChartColumn className="h-3.5 w-3.5" /> <Trans i18nKey="attendance.viewReport" />
                    </Button>
                  </td>
                  <td className="px-4 py-3.5">
                    <Button
                    variant="outline"
                    className="h-9 rounded-xl px-3 text-xs font-semibold border-(--color-text-border) hover:border-primary/40 hover:text-primary cursor-pointer"
                    onClick={() => setTomorrowPlanRecord(record)}>

                      <CalendarDays className="h-3.5 w-3.5" /> <Trans i18nKey="attendance.tomorrowPlan" />
                    </Button>
                  </td>
                 <td className="px-4 py-3.5 text-right">
                   {record.attendanceStatus === "Pending_Manager_Approval" ? (
                     <span className="text-xs font-semibold per-file-hint">
                       <Trans i18nKey="attendance.pendingApproval" defaults="Pending Approval" />
                     </span>
                   ) : !hasAppliedRegularization(record.regularizationStatus) ? (
                     <Button
                       variant="outline"
                       className="h-9 rounded-xl px-3 text-xs font-semibold border-(--color-text-border) hover:border-primary/40 hover:text-primary cursor-pointer"
                       onClick={(e) => {
                         e.stopPropagation();
                         navigate("/hrms-ess/attendance/regularization", {
                           state: { date: dateStr, attendanceId: record.attendanceId }
                         });
                       }}>
                       <CalendarClock className="h-3.5 w-3.5" /> <Trans i18nKey="attendance.regularize" />
                     </Button>
                   ) : (
                     <span className="text-xs per-file-hint">{record.regularizationStatus}</span>
                   )}
                 </td>
                </tr>);

          })}
        </DataTable>

        <div className="md:hidden">
          {loading &&
          <div className="px-4 py-8 text-center per-file-hint">
              <Trans i18nKey="attendance.loadingAttendanceRecords" />
            </div>
          }
          {!loading && error &&
          <div className="px-4 py-8 text-center text-error">{error}</div>
          }
          {!loading && !error && records.length === 0 &&
          <div className="px-4 py-8 text-center per-file-hint">
              <Trans i18nKey="attendance.noAttendanceRecordsFound" />
            </div>
          }
          {!loading &&
          !error &&
          records.map((record, i) => {
            const dateStr =
            record.attendanceDate || record.date || (record.checkInTime ? record.checkInTime.split("T")[0] : "");
            return (
              <div
                key={record.attendanceId || i}
                className="px-4 py-4 space-y-2.5 border-b-2 border-(--color-text-border)">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{formatDate(dateStr)}</p>
                    </div>
                  {record.attendanceStatus === "Pending_Manager_Approval" ? (
                    <span className="text-[11px] font-semibold per-file-hint shrink-0">
                      <Trans i18nKey="attendance.pendingApproval" defaults="Pending Approval" />
                    </span>
                  ) : !hasAppliedRegularization(record.regularizationStatus) ? (
                    <Button
                      variant="outline"
                      className="h-8 shrink-0 rounded-xl px-2.5 text-[11px] font-semibold border-(--color-text-border) hover:border-primary/40 hover:text-primary cursor-pointer"
                      onClick={() => {
                        navigate("/hrms-ess/attendance/regularization", {
                          state: { date: dateStr, attendanceId: record.attendanceId }
                        });
                      }}>
                      <CalendarClock className="h-3.5 w-3.5" /> <Trans i18nKey="attendance.regularize" />
                    </Button>
                  ) : (
                    <span className="text-[11px] font-semibold per-file-hint shrink-0">
                      {record.regularizationStatus}
                    </span>
                  )}
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="flex flex-col items-center gap-1.5 px-1 py-2.5 text-center rounded-xl border border-(--color-text-border)/60">
                      <div className="h-9 w-9 rounded-full bg-workspace/40 grid place-items-center">
                        <LogIn className="h-4 w-4" />
                      </div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide per-file-hint">
                        <Trans i18nKey="attendance.checkIn" />
                      </p>
                      <p className="font-mono text-sm font-semibold leading-tight">{formatTime(record.checkInTime)}</p>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 px-1 py-2.5 text-center rounded-xl border border-(--color-text-border)/60">
                      <div className="h-9 w-9 rounded-full bg-workspace/40 grid place-items-center">
                        <LogOut className="h-4 w-4" />
                      </div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide per-file-hint">
                        <Trans i18nKey="attendance.checkOut" />
                      </p>
                      <p className="font-mono text-sm font-semibold leading-tight">{formatTime(record.checkOutTime)}</p>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 px-1 py-2.5 text-center rounded-xl border border-(--color-text-border)/60">
                      <div className="h-9 w-9 rounded-full bg-workspace/40 grid place-items-center">
                        <Timer className="h-4 w-4" />
                      </div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide per-file-hint">
                        <Trans i18nKey="dateTime.duration" />
                      </p>
                      <p className="font-mono text-sm font-semibold leading-tight">
                        {formatDuration(record.totalWorkMinutes ?? record.workMinutes)}
                      </p>
                    </div>
                  </div>

<div className="grid grid-cols-3 gap-2 pt-1 border-(--color-text-border)/60">                    <button
                    type="button"
                    onClick={() => setTimeLogRecord(record)}
                    className="flex flex-col items-center justify-center gap-1 rounded-xl border border-(--color-text-border)/60 py-2.5 text-[11px] font-semibold text-foreground hover:border-primary/40 hover:text-primary">

                      <Clock3 className="h-4 w-4" />
                      <Trans i18nKey="attendance.viewTimeLog" />
                    </button>
                    <button
                    type="button"
                    onClick={() => setViewReportRecord(record)}
                    className="flex flex-col items-center justify-center gap-1 rounded-xl border border-(--color-text-border)/60 py-2.5 text-[11px] font-semibold text-foreground hover:border-primary/40 hover:text-primary">

                      <ChartColumn className="h-4 w-4" />
                      <Trans i18nKey="attendance.viewReport" />
                    </button>
                    <button
                    type="button"
                    onClick={() => setTomorrowPlanRecord(record)}
                    className="flex flex-col items-center justify-center gap-1 rounded-xl border border-(--color-text-border)/60 py-2.5 text-[11px] font-semibold text-foreground hover:border-primary/40 hover:text-primary">

                      <CalendarDays className="h-4 w-4" />
                      <Trans i18nKey="attendance.tomorrowPlan" />
                    </button>
                  </div>
                </div>);
          })}
        </div>

        {!loading && !error && (records.length > 0 || viewMode === "day") &&
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-(--color-text-border)/60 text-xs per-file-hint">
            <span>
              {viewMode === "day" ?
              formatDate(appliedFilters.startDate) :

              <>{records.length} <Trans i18nKey="common.of" /> {records.length}</>
              }
            </span>
            {viewMode === "day" &&
          <div className="flex items-center gap-1">
                <button
              type="button"
              onClick={goToPrevDay}
              className="h-8 px-3 inline-flex items-center gap-1 rounded-lg border border-(--color-text-border) hover:bg-workspace/40 text-xs font-semibold">

                  ‹ <Trans i18nKey="pagination.prev" defaults="Prev" />
                </button>
                <button
              type="button"
              disabled={isNextDayDisabled}
              onClick={goToNextDay}
              className="h-8 px-3 inline-flex items-center gap-1 rounded-lg border border-(--color-text-border) hover:bg-workspace/40 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-semibold">

                  <Trans i18nKey="pagination.next" defaults="Next" /> ›
                </button>
              </div>
          }
          </div>
        }
      </ContentCard>

      <TimeLogModal
        record={timeLogRecord}
        onOpenChange={(open) => {
          if (!open) setTimeLogRecord(null);
        }} />

    <ViewReportModal
    record={viewReportRecord}
    onOpenChange={(open) => {
        if (!open) setViewReportRecord(null); }} />

    <TomorrowPlanModal
      record={tomorrowPlanRecord}
      onOpenChange={(open) => {
        if (!open) setTomorrowPlanRecord(null);
      }}
    />

    </AppLayout>);

}
